<template>
    <report-layout
        :report="report"
        :identity-verified="identity_verified"
        current-step="payment"
    >
        <!-- 86bce6r42: the report is not pulled until it is paid for, so what sits
             behind the popup is a placeholder, never the renter's data. -->
        <!-- Copy from Rich (ClickUp, 2026-10-07). -->
        <div class="bg-white rounded-lg shadow-md p-6" data-test="report-ready">
            <h2 class="text-2xl font-bold text-gray-900">{{ heading }}</h2>
            <p class="mt-1 text-sm text-gray-600">Unlock to view your personalized background check</p>
            <button
                type="button"
                class="primaryButton mt-4"
                data-test="unlock-my-report"
                @click="openCheckout"
            >
                Unlock My Report
            </button>
        </div>

        <!-- 86bcfa7n0: every placeholder section has its own way into checkout. -->
        <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 select-none" data-test="report-placeholder">
            <div v-for="section in placeholderSections" :key="section" class="bg-white rounded-lg shadow-md p-6">
                <h3 class="font-bold text-gray-700">{{ section }}</h3>
                <div class="relative mt-4">
                    <div class="space-y-3 blur-sm" aria-hidden="true">
                        <div class="h-3 w-11/12 rounded bg-gray-300" />
                        <div class="h-3 w-3/4 rounded bg-gray-300" />
                        <div class="h-3 w-5/6 rounded bg-gray-300" />
                        <div class="h-3 w-2/3 rounded bg-gray-300" />
                    </div>
                    <div class="absolute inset-0 flex items-center justify-center">
                        <button
                            type="button"
                            class="inline-flex justify-center rounded-md border border-rent-risk-blue-400 bg-white px-4 py-2 text-sm font-semibold text-rent-risk-blue-400 shadow-sm hover:bg-rent-risk-blue-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            data-test="unlock-section"
                            @click="openCheckout"
                        >
                            Unlock My Report
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <self-check-unlock-dialog
            :show="showCheckout"
            :report-id="report.id"
            :checkout="checkout"
            :seconds-left="secondsLeft"
            :countdown="countdown"
            @close="requestLeave({ type: 'close' })"
        />

        <self-check-leaving-offer
            :show="showLeavingOffer"
            :percent="checkout.leaving.percent"
            :countdown="countdown"
            :processing="acceptingLeavingOffer"
            @accept="acceptLeavingOffer"
            @decline="declineLeavingOffer"
        />
    </report-layout>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import ReportLayout from './ReportLayout.vue'
import SelfCheckUnlockDialog from '@/Components/SelfCheckUnlockDialog.vue'
import SelfCheckLeavingOffer from '@/Components/SelfCheckLeavingOffer.vue'
import { useCountdown } from './countdown.js'

/**
 * 86bce6r42: "your custom report is ready". The renter has accepted the terms
 * and authenticated with Experian; they pay in a popup here and the report is
 * pulled once they have.
 *
 * Trying to leave checkout while the discount countdown runs — closing the
 * popup, the browser's back button, or a link elsewhere in the app — offers an
 * extra 10% first, every time, until they take it or the countdown ends.
 */
const props = defineProps({
    report: {
        type: Object,
        required: true,
    },
    identity_verified: {
        type: Boolean,
        default: true,
    },
    checkout: {
        type: Object,
        required: true,
    },
})

const placeholderSections = ['Credit summary', 'Criminal and eviction history', 'Collections', 'Public records']

const firstName = computed(() => (usePage().props.auth?.user?.name ?? '').trim().split(/\s+/)[0])

const heading = computed(() => (firstName.value ? `${firstName.value}, your custom report is ready` : 'Your custom report is ready'))

const { secondsLeft, countdown } = useCountdown(() => props.checkout.discount.seconds_left)

const showCheckout = ref(false)
const showLeavingOffer = ref(false)
const acceptingLeavingOffer = ref(false)
// What the renter was doing when the offer interrupted them: closing the
// popup, going back, or following a link (with the visit to replay).
const pendingLeave = ref(null)

const offersLeavingDiscount = computed(() => props.checkout.leaving.offered && secondsLeft.value > 0)

function openCheckout() {
    showCheckout.value = true

    // The countdown starts the first time the popup opens, and only then.
    if (!props.checkout.discount.started) {
        router.post(route('reports.checkout.start', props.report.id), {}, {
            preserveScroll: true,
            preserveState: true,
        })
    }
}

/**
 * Every way out of checkout comes through here: the offer if it still
 * stands, otherwise straight on.
 */
function requestLeave(leave) {
    if (offersLeavingDiscount.value) {
        pendingLeave.value = leave
        showCheckout.value = false
        showLeavingOffer.value = true
        return
    }

    leaveNow(leave)
}

function leaveNow(leave) {
    if (leave.type === 'close') {
        showCheckout.value = false
    } else if (leave.type === 'back') {
        // Past the extra history entry and the page itself.
        disarmBackButton()
        window.history.go(-2)
    } else if (leave.type === 'visit') {
        bypassLinkGuard = true
        router.visit(leave.url, { method: leave.method })
    }
}

function acceptLeavingOffer() {
    acceptingLeavingOffer.value = true
    router.post(route('reports.discount.leaving', props.report.id), {}, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            showLeavingOffer.value = false
            pendingLeave.value = null
            showCheckout.value = true
        },
        onFinish: () => {
            acceptingLeavingOffer.value = false
        },
    })
}

function declineLeavingOffer() {
    showLeavingOffer.value = false
    const leave = pendingLeave.value ?? { type: 'close' }
    pendingLeave.value = null
    leaveNow(leave)
}

/*
 * Browser back. app.js reloads the page on every popstate and Inertia restores
 * its own history entry, so the page keeps one extra entry of its own on top.
 * Back while the offer stands lands on that entry: it is put back and the
 * offer shown. Back once the offer is gone goes one further, to where the
 * renter came from.
 *
 * Window popstate listeners run in the order they were added, and app.js's
 * `window.onpopstate` was added first, before Inertia's. The page takes that
 * slot while armed, so it runs ahead of both and can stop them, and gives it
 * back when it lets go.
 *
 * Chrome's back button skips an entry a page added without the user touching
 * it, so the entry is added on the renter's first click or key press here
 * (opening the popup is one), never on load.
 */
let backButtonArmed = false
let originalOnPopState = null

function onPopState(event) {
    if (!backButtonArmed) {
        return originalOnPopState?.call(window, event)
    }

    event.stopImmediatePropagation()

    if (offersLeavingDiscount.value) {
        window.history.pushState(window.history.state, '', window.location.href)
        requestLeave({ type: 'back' })
        return
    }

    disarmBackButton()
    window.history.back()
}

// Whether the offer can still come up: not taken, and the countdown not over.
const leavingOfferStillPossible = computed(() => !props.checkout.leaving.applied
    && (!props.checkout.discount.started || offersLeavingDiscount.value))

function armBackButton() {
    if (backButtonArmed || !leavingOfferStillPossible.value) {
        return
    }

    window.history.pushState(window.history.state, '', window.location.href)
    originalOnPopState = window.onpopstate
    window.onpopstate = onPopState
    backButtonArmed = true
}

function disarmBackButton() {
    if (!backButtonArmed) {
        return
    }

    backButtonArmed = false
    window.onpopstate = originalOnPopState
    originalOnPopState = null
}

/*
 * Links elsewhere in the app. Only page visits away from here: the popup's own
 * posts (discount, countdown start) and reloads of this page go through.
 */
let bypassLinkGuard = false
let removeLinkGuard = null

function guardLinks(event) {
    const visit = event.detail.visit
    const leavingThePage = visit.method === 'get' && visit.url.pathname !== window.location.pathname

    if (bypassLinkGuard || !leavingThePage || !offersLeavingDiscount.value) {
        return true
    }

    requestLeave({ type: 'visit', url: visit.url.href, method: visit.method })

    return false
}

onMounted(() => {
    removeLinkGuard = router.on('before', guardLinks)
    document.addEventListener('pointerdown', armBackButton, true)
    document.addEventListener('keydown', armBackButton, true)
})

onBeforeUnmount(() => {
    removeLinkGuard?.()
    document.removeEventListener('pointerdown', armBackButton, true)
    document.removeEventListener('keydown', armBackButton, true)
    disarmBackButton()
})
</script>
